import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { isIOS } from '../lib/capacitor';
import { API_URL } from '../lib/apiUrl';
import './TripPage.css';

const POLL_INTERVAL = 10000; // 10 seconds

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom car icon
const carIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#22c55e" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#22c55e"/>
      <path d="M7 13l2-5h6l2 5M7 13h10M7 13v2a1 1 0 001 1h1a1 1 0 001-1v-2M17 13v2a1 1 0 01-1 1h-1a1 1 0 01-1-1v-2" stroke="white" stroke-width="1.5" fill="none"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Component to update map center when location changes
function MapUpdater({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  return null;
}

export default function TripPage() {
  const { shortCode } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const pollIntervalRef = useRef(null);

  const fetchTrip = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shared-trips/${shortCode}`);
      const data = await response.json();

      if (response.ok) {
        setTrip(data);
        if (data.location_updated_at) {
          setLastUpdate(new Date(data.location_updated_at));
        }
        return data;
      } else {
        setError(data.error || 'Trip not found');
        return null;
      }
    } catch (err) {
      setError('Could not load trip details');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shortCode) {
      fetchTrip();
    }

    // Start polling for updates
    pollIntervalRef.current = setInterval(async () => {
      const data = await fetchTrip();
      // Stop polling if tracking is no longer active
      if (data && !data.tracking_active) {
        clearInterval(pollIntervalRef.current);
      }
    }, POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [shortCode]);

  const vehicleInfo = trip?.vehicle_info;
  const vehicleDesc = vehicleInfo
    ? `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.trim()
    : 'Unknown Vehicle';

  // Update document title and OG meta tags for link previews
  useEffect(() => {
    if (!trip) return;
    const plate = trip.plate || 'Unknown';
    const licensed = trip.is_licensed ? 'Licensed' : 'Not Found';
    document.title = `${plate} — ${licensed} | RideshareChecker`;

    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('og:title', `${vehicleDesc} (${plate}) — ${licensed}`);
    setMeta('og:description', `Shared ride check: ${vehicleDesc}, plate ${plate}. ${trip.is_licensed ? 'Vehicle is licensed.' : 'Vehicle not found in registry.'}`);

    return () => { document.title = 'RideshareChecker | Verify Uber, Lyft & Taxi'; };
  }, [trip, vehicleDesc]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatLastUpdate = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return formatTime(date.toISOString());
  };

  const openInMaps = () => {
    if (trip?.latitude && trip?.longitude) {
      window.open(`https://maps.google.com/maps?q=${trip.latitude},${trip.longitude}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="trip-page">
        <div className="trip-loading">
          <div className="trip-spinner"></div>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-page">
        <div className="trip-error">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <h2>{error}</h2>
          <p>This trip link may have expired or doesn't exist.</p>
          <Link to="/" className="trip-home-link">Go to RideshareChecker</Link>
        </div>
      </div>
    );
  }

  const hasLocation = trip?.latitude && trip?.longitude;
  const position = hasLocation ? [trip.latitude, trip.longitude] : null;

  return (
    <div className="trip-page">
      <div className="trip-container">
        <header className="trip-header">
          <Link to="/" className="trip-logo">
            RideshareChecker
          </Link>
          <span className="trip-badge-header">Shared Trip</span>
        </header>

        {/* Live Map */}
        {hasLocation && (
          <div className="trip-map-container">
            {trip.tracking_active && (
              <div className="live-indicator">
                <span className="live-dot"></span>
                LIVE
                {lastUpdate && (
                  <span className="last-update-time">Updated {formatLastUpdate(lastUpdate)}</span>
                )}
              </div>
            )}
            <MapContainer
              center={position}
              zoom={15}
              className="trip-map"
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={position} icon={carIcon}>
                <Popup>
                  <strong>{vehicleDesc}</strong><br />
                  {trip.plate} ({trip.state})<br />
                  {trip.location_name && <span>{trip.location_name}</span>}
                </Popup>
              </Marker>
              <MapUpdater position={position} />
            </MapContainer>
            <button className="map-open-btn" onClick={openInMaps}>
              Open in Google Maps
            </button>
          </div>
        )}

        <div className="trip-card">
          {trip.car_image && (
            <div className="trip-image-container">
              <div className="trip-image-shimmer" />
              <img
                src={trip.car_image}
                alt={vehicleDesc}
                className="trip-car-image"
                onLoad={(e) => {
                  e.target.classList.add('loaded');
                  const shimmer = e.target.previousSibling;
                  if (shimmer) shimmer.style.display = 'none';
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const shimmer = e.target.previousSibling;
                  if (shimmer) shimmer.style.display = 'none';
                }}
              />
            </div>
          )}

          <div className="trip-details">
            <div className="trip-plate-section">
              <div className="trip-plate">{trip.plate}</div>
              <div className="trip-state">{trip.state}</div>
            </div>

            <div className="trip-vehicle-name">{vehicleDesc}</div>

            <div className={`status-badge badge-full ${trip.is_licensed === true ? 'badge-licensed' : trip.is_licensed === false ? 'badge-not-licensed' : 'badge-unverified'}`}>
              {trip.is_licensed === true ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Found in {trip.state === 'IL' || trip.tlc_data?.license_type?.includes('CHICAGO') ? 'Chicago BACP' : 'NYC TLC'} Registry
                </>
              ) : trip.is_licensed === false ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  No Record Found
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  Working on {trip.state} registry access
                </>
              )}
            </div>

            {vehicleInfo?.color && (
              <div className="trip-detail-row">
                <span className="trip-detail-label">Color</span>
                <span className="trip-detail-value">{vehicleInfo.color}</span>
              </div>
            )}

            {trip.tlc_data?.base_name && (
              <div className="trip-detail-row">
                <span className="trip-detail-label">Company</span>
                <span className="trip-detail-value">{trip.tlc_data.base_name}</span>
              </div>
            )}

            {trip.location_name && (
              <div className="trip-location" onClick={openInMaps}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>{trip.location_name}</span>
              </div>
            )}

            <div className="trip-meta">
              <span>Shared {formatTime(trip.created_at)}</span>
              <span className="trip-expires">Expires {formatTime(trip.expires_at)}</span>
            </div>
          </div>
        </div>

        <div className="trip-safety-note">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
          <span>Always verify the vehicle and driver before getting in.</span>
        </div>

        <footer className="trip-footer">
          <Link to="/" className="trip-cta">
            Check Your Own Ride
          </Link>
          <p className="trip-footer-text">
            {isIOS()
              ? 'Powered by RideshareChecker'
              : <>Powered by <a href="https://ridesharechecker.com">RideshareChecker.com</a></>}
          </p>
        </footer>
      </div>
    </div>
  );
}
